import { API_BASE_URL } from "../../../configurations/configurations";
import { mainApiSlice } from "../api/mainApiSlice";

export const projectsApi = mainApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // get a single project
    getProjectByProjectId: builder.query({
      query: ({ projectId }) => ({
        url: `${API_BASE_URL}/projects/project/${projectId}`,
        method: "GET",
      }),
      provideTags: (projectId) => [{ type: "Project", id: projectId }],
    }),

    // get user's projects
    getProjectsByUserId: builder.query({
      query: ({ userId, page, queryFilters }) => {
        let params = new URLSearchParams();
        params.append("page", page);
        if (Object.keys(queryFilters).length > 0) {
          Object.keys(queryFilters).forEach((key) => {
            if (queryFilters[key]) {
              params.append(key, queryFilters[key]);
            }
          });
        }
        return {
          url: `${API_BASE_URL}/projects/${userId}?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: (result) => {
        if (result && Array.isArray(result.paginated_results)) {
          return [
            ...result.paginated_results.map(({ id }) => ({
              type: "Project",
              id,
            })),
            { type: "ProjectList", id: "LIST" },
          ];
        }
        return [{ type: "ProjectList", id: "LIST" }];
      },
    }),
    // get projects by admin_id and group them by year/month for statistics
    getGroupedProjectsByAdminId: builder.query({
      query: ({ adminId }) => ({
        url: `${API_BASE_URL}/projects/${adminId}/statistics?search_status=completed`,
        method: "GET",
      }),
      providesTags: ["ProjectList"],
    }),

    // get projects by admin_id and status
    getProjectsByAdminIdAndStatus: builder.query({
      query: ({ adminId, projectStatus }) => ({
        url: `${API_BASE_URL}/projects/${adminId}/${projectStatus}`,
        method: "GET",
      }),
      providesTags: ["ProjectList"],
    }),
    // update project by project id
    updateSingleProject: builder.mutation({
      query: ({ projectId, userId, formData }) => ({
        url: `${API_BASE_URL}/projects/${projectId}/${userId}`,
        method: "PUT",
        body: formData,
        formData: true,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: "Project", id: projectId },
        { type: "ProjectList", id: "LIST" },
      ],
    }),
    // to populate the select fields in the new project form
    getVehicleInfoTypes: builder.query({
      query: () => ({
        url: `${API_BASE_URL}/map-pack/types`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoTypes"],
      invalidatesTags: [
        "VehicleInfoBrands",
        "VehicleInfoModels",
        "VehicleInfoGenerations",
        "VehicleInfoEngines",
        "VehicleInfoEngineHps",
        "VehicleInfoYears",
        "VehicleInfoEcuTypes",
        "VehicleInfoEcuBrands",
        "VehicleInfoEcuNames",
      ],
    }),
    getVehicleInfoBrands: builder.query({
      query: (typeId) => ({
        url: `${API_BASE_URL}/map-pack/brands-by-vehicle-type/${typeId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoBrands"],
      invalidatesTags: [
        "VehicleInfoModels",
        "VehicleInfoGenerations",
        "VehicleInfoEngines",
        "VehicleInfoEngineHps",
        "VehicleInfoYears",
        "VehicleInfoEcuTypes",
        "VehicleInfoEcuBrands",
        "VehicleInfoEcuNames",
      ],
    }),
    getVehicleInfoModels: builder.query({
      query: (brandId) => ({
        url: `${API_BASE_URL}/map-pack/models-by-brand/${brandId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoModels"],
      invalidatesTags: [
        "VehicleInfoGenerations",
        "VehicleInfoEngines",
        "VehicleInfoEngineHps",
        "VehicleInfoYears",
        "VehicleInfoEcuTypes",
        "VehicleInfoEcuBrands",
        "VehicleInfoEcuNames",
      ],
    }),
    getVehicleInfoGenerations: builder.query({
      query: ({ brandId, modelId }) => ({
        url: `${API_BASE_URL}/map-pack/generations-by-brand-model/${brandId}/${modelId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoGenerations"],
      invalidatesTags: [
        "VehicleInfoEngines",
        "VehicleInfoEngineHps",
        "VehicleInfoYears",
        "VehicleInfoEcuTypes",
        "VehicleInfoEcuBrands",
        "VehicleInfoEcuNames",
      ],
    }),
    getVehicleInfoEngines: builder.query({
      query: ({ brandId, modelId, generationId }) => ({
        url: `${API_BASE_URL}/map-pack/engines-by-brand-model-generation/${brandId}/${modelId}/${generationId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoEngines"],
      invalidatesTags: [
        "VehicleInfoEngineHps",
        "VehicleInfoYears",
        "VehicleInfoEcuTypes",
        "VehicleInfoEcuBrands",
        "VehicleInfoEcuNames",
      ],
    }),
    getVehicleInfoEngineHps: builder.query({
      query: (engineId) => ({
        url: `${API_BASE_URL}/map-pack/engines-hp-by-engine/${engineId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoEngineHps"],
      invalidatesTags: [
        "VehicleInfoYears",
        "VehicleInfoEcuTypes",
        "VehicleInfoEcuBrands",
        "VehicleInfoEcuNames",
      ],
    }),
    getVehicleInfoYears: builder.query({
      query: ({ engineId, engineHpId }) => ({
        url: `${API_BASE_URL}/map-pack/years-by-engine-engine-hp/${engineId}/${engineHpId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoYears"],
      invalidatesTags: [
        "VehicleInfoEcuTypes",
        "VehicleInfoEcuBrands",
        "VehicleInfoEcuNames",
      ],
    }),
    getVehicleInfoEcuTypes: builder.query({
      query: ({
        typeId,
        brandId,
        modelId,
        generationId,
        engineId,
        engineHpId,
        yearId,
        fuelId,
      }) => ({
        url: `${API_BASE_URL}/map-pack/ecu-types/${typeId}/${brandId}/${modelId}/${generationId}/${engineId}/${engineHpId}/${yearId}/${fuelId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoEcuTypes"],
      invalidatesTags: ["VehicleInfoEcuBrands", "VehicleInfoEcuNames"],
    }),
    getVehicleInfoEcuBrands: builder.query({
      query: ({
        typeId,
        brandId,
        modelId,
        generationId,
        engineId,
        engineHpId,
        yearId,
        fuelId,
        ecuTypeId,
      }) => ({
        url: `${API_BASE_URL}/map-pack/ecu-brands-by-ecu-type/${typeId}/${brandId}/${modelId}/${generationId}/${engineId}/${engineHpId}/${yearId}/${fuelId}/${ecuTypeId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoEcuBrands"],
      invalidatesTags: ["VehicleInfoEcuNames"],
    }),
    getVehicleInfoEcuNames: builder.query({
      query: ({
        typeId,
        brandId,
        modelId,
        generationId,
        engineId,
        engineHpId,
        yearId,
        fuelId,
        ecuTypeId,
        ecuBrandId,
      }) => ({
        url: `${API_BASE_URL}/map-pack/ecu-names-by-ecu-brand/${typeId}/${brandId}/${modelId}/${generationId}/${engineId}/${engineHpId}/${yearId}/${fuelId}/${ecuTypeId}/${ecuBrandId}`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoEcuNames"],
    }),

    // start -- indipendent from the vehicle
    getVehicleInfoGearboxes: builder.query({
      query: () => ({
        url: `${API_BASE_URL}/map-pack/gearboxes`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoGearboxes"],
    }),
    getVehicleInfoFuels: builder.query({
      query: () => ({
        url: `${API_BASE_URL}/map-pack/fuels`,
        method: "GET",
      }),
      providesTags: ["VehicleInfoFuels"],
    }),

    getVehicleInfoReadingMethods: builder.query({
      query: () => ({
        url: `${API_BASE_URL}/reading-methods`,
        method: "GET",
      }),
    }),

    // end --- indipendent from the vehicle
    addProject: builder.mutation({
      query: ({ userId, formData }) => ({
        url: `${API_BASE_URL}/projects/${userId}/create`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "Projects", id: "LIST" }],
    }),
  }),
});

export const {
  useGetProjectByProjectIdQuery,
  useGetProjectsByUserIdQuery,
  useGetGroupedProjectsByAdminIdQuery,
  useGetProjectsByAdminIdAndStatusQuery,
  useUpdateSingleProjectMutation,
  useGetVehicleInfoTypesQuery,
  useGetVehicleInfoBrandsQuery,
  useGetVehicleInfoModelsQuery,
  useGetVehicleInfoEnginesQuery,
  useGetVehicleInfoEngineHpsQuery,
  useGetVehicleInfoGearboxesQuery,
  useGetVehicleInfoGenerationsQuery,
  useGetVehicleInfoYearsQuery,
  useGetVehicleInfoFuelsQuery,
  useGetVehicleInfoReadingMethodsQuery,
  useGetVehicleInfoEcuBrandsQuery,
  useGetVehicleInfoEcuNamesQuery,
  useGetVehicleInfoEcuTypesQuery,
  useAddProjectMutation,
} = projectsApi;
